import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, useReducedMotion } from 'framer-motion';
import { AlertTriangle, Check, History, Library, Link2Off, Save, Send } from 'lucide-react';
import { useClerk } from '@clerk/clerk-react';
import { useApp } from '../../../contexts/AppContext';
import { useClerkAuth } from '../../../hooks/useClerkAuth';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useToast } from '../../ui/Toast';
import Button from '../../ui/Button';
import { PLAYLIST_MAX_ITEMS, formatWeekdayMonthDay } from '../../../utils/playlist';
import { DELIVERY_ACTIVE, isStationOnline, stationLabel } from '../../../utils/playlistDelivery';
import { describeLibraryLine } from '../../../utils/stationLibrary';
import { haptics } from '../../../utils/haptics';
import PlaylistHeader from './PlaylistHeader';
import PlaylistPicker from './PlaylistPicker';
import PlaylistCarousel from './PlaylistCarousel';
import PlaylistBanner from './PlaylistBanner';
import AddFlight from './AddFlight';
import DeliverConfirmSheet from './DeliverConfirmSheet';
import SavedPlaylistsDrawer from './SavedPlaylistsDrawer';
import TileConfirmPopover from './TileConfirmPopover';
import { useDeliveryAnnouncements } from './useDeliveryAnnouncements';
import {
  COPY,
  SAVED_COPY,
  announce as copy,
  deleteFailed,
  deliveredAtCaption,
  plannerHeading,
  restoredBanner,
  saveFailed,
  savedAnnounce,
  savedCaption,
} from './playlistCopy';

const ONBOARDED_KEY = 'turbosync:playlist-onboarded:v1';
const DELIVER_REASON_ID = 'playlist-deliver-reason';
const SHORT_VIEWPORT_PX = 560;

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return true;
  }
};

const writeFlag = (key) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Private mode: the pulse simply shows again next visit.
  }
};

/**
 * /playlist — compose a Service Playlist: pick designs (add mode), order
 * them in the always-visible carousel, deliver to ProPresenter.
 */
export default function PlaylistPlanner() {
  const {
    state,
    isApiReady,
    fetchLinkedAgents,
    addToPlaylist,
    setPlaylistMeta,
    clearPlaylist,
    dismissRestoredPlaylist,
    dismissInterruptedDelivery,
    fetchStationSettings,
    openDeliverConfirmation,
    cancelDeliverConfirmation,
    deliverPlaylist,
    retryPlaylistDelivery,
    fetchPlaylists,
    savePlaylist,
    openPlaylist,
    deletePlaylist,
    newPlaylist,
  } = useApp();
  const clerk = useClerk();
  const { hasCanvaConnected } = useClerkAuth();
  const toast = useToast();
  const reduceMotion = useReducedMotion();
  const isWide = useMediaQuery('(min-width: 768px)');
  const layout = isWide ? 'strip' : 'sheet';

  const {
    playlist,
    linkedAgents,
    playlistRestoredFrom,
    playlistInterrupted,
    stationSettings,
    stationSettingsStatus,
    savedPlaylists,
    savedPlaylistsStatus,
  } = state;
  const { items, delivery } = playlist;
  const canvaConnected = hasCanvaConnected();
  const locked = DELIVERY_ACTIVE.includes(delivery.status);
  const confirming = delivery.status === 'confirming';
  const capReached = items.length >= PLAYLIST_MAX_ITEMS;
  const hasOnlineStation = linkedAgents.some((agent) => agent.status === 'online');

  const [stripOpen, setStripOpen] = useState(() => window.innerHeight >= SHORT_VIEWPORT_PX);
  const [sheetOpen, setSheetOpen] = useState(false);
  const dockOpen = isWide ? stripOpen : sheetOpen;
  const setDockOpen = isWide ? setStripOpen : setSheetOpen;

  const [message, setMessage] = useState({ text: '', id: 0 });
  const announce = useCallback((text) => setMessage((m) => ({ text, id: m.id + 1 })), []);
  const [alert, setAlert] = useState({ text: '', id: 0 });
  const announceAlert = useCallback((text) => setAlert((m) => ({ text, id: m.id + 1 })), []);
  useDeliveryAnnouncements({ delivery, items, linkedAgents, announce, announceAlert });

  // The dock is forced open while a delivery runs (spec S5).
  useEffect(() => {
    if (locked) setDockOpen(true);
  }, [locked, setDockOpen]);

  useEffect(() => {
    if (confirming && stationSettingsStatus === 'idle') fetchStationSettings();
  }, [confirming, stationSettingsStatus, fetchStationSettings]);

  // Focus goes back to Deliver on cancel (the Modal default) and to the strip on confirm.
  const confirmReturnRef = useRef(null);
  useEffect(() => {
    if (confirming) confirmReturnRef.current = null;
  }, [confirming]);
  const handleConfirm = useCallback((agentId) => {
    confirmReturnRef.current = () => document.querySelector('[data-delivery-strip]');
    deliverPlaylist(agentId);
  }, [deliverPlaylist]);

  const [flight, setFlight] = useState(null);
  const handleLanded = useCallback(() => setFlight(null), []);

  const [searchPulse] = useState(() => !readFlag(ONBOARDED_KEY));
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchPulse) writeFlag(ONBOARDED_KEY);
  }, [searchPulse]);

  useEffect(() => {
    if (isApiReady) fetchLinkedAgents();
  }, [isApiReady, fetchLinkedAgents]);

  // Saved playlists (S8). Fetched on this page only.
  useEffect(() => {
    if (isApiReady) fetchPlaylists();
  }, [isApiReady, fetchPlaylists]);

  const savingAvailable = savedPlaylistsStatus !== 'not_configured';
  const isSaved = Boolean(playlist.savedUpdatedAt);
  const draftNeedsConfirm = playlist.dirty && items.length > 0;
  const canSave = savingAvailable && items.length > 0 && !locked;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const [saveIssue, setSaveIssue] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!playlist.savedUpdatedAt) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [playlist.savedUpdatedAt]);

  const runSave = useCallback(async (options) => {
    setSaving(true);
    try {
      const result = await savePlaylist(options);
      if (result.ok) announce(savedAnnounce.saved(result.row.name));
      else if (result.reason === 'error') toast.error(saveFailed(result.error.message));
      return result;
    } finally {
      setSaving(false);
    }
  }, [savePlaylist, announce, toast]);

  const handleSave = async (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const result = await runSave();
    if (result.reason === 'conflict' || result.reason === 'playlist_not_found') setSaveIssue({ kind: result.reason, rect });
  };

  const handleOpenSaved = useCallback((id) => {
    const opened = openPlaylist(id);
    setDrawerOpen(false);
    if (opened) {
      if (isWide) setDockOpen(true);
      announce(savedAnnounce.opened(opened.name, opened.items.length));
    }
  }, [openPlaylist, isWide, setDockOpen, announce]);

  const handleNewPlaylist = useCallback(() => {
    newPlaylist();
    setDrawerOpen(false);
  }, [newPlaylist]);

  const handleSaveThen = useCallback(async (action) => {
    const result = await runSave(action.forceSave ? { force: true } : undefined);
    if (!result.ok) {
      if (result.reason === 'conflict') toast.warning(SAVED_COPY.conflict);
      else if (result.reason === 'playlist_not_found') toast.warning(SAVED_COPY.goneTitle);
      return;
    }
    if (action.type === 'open') handleOpenSaved(action.id);
    else handleNewPlaylist();
  }, [runSave, toast, handleOpenSaved, handleNewPlaylist]);

  const handleDeleteSaved = useCallback(async (row) => {
    setDeletingIds((ids) => [...ids, row.id]);
    const result = await deletePlaylist(row.id);
    setDeletingIds((ids) => ids.filter((id) => id !== row.id));
    if (result.ok) announce(savedAnnounce.deleted(row.name));
    else if (result.reason === 'error') toast.error(deleteFailed(result.error.message));
  }, [deletePlaylist, announce, toast]);

  const resolveSaveIssue = async (choice) => {
    const issue = saveIssue;
    setSaveIssue(null);
    if (choice === 'useSaved') handleOpenSaved(playlist.id);
    else if (issue?.kind === 'conflict') await runSave({ force: true });
    else await runSave({ asNew: true });
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerHeight < SHORT_VIEWPORT_PX) setStripOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const countsByDesignId = useMemo(() => {
    const counts = new Map();
    items.forEach(({ designId }) => counts.set(designId, (counts.get(designId) ?? 0) + 1));
    return counts;
  }, [items]);

  const focusSearch = useCallback(() => {
    setSheetOpen(false);
    const input = searchInputRef.current;
    if (!input) return;
    input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    input.focus({ preventScroll: true });
  }, [reduceMotion]);

  const focusFirstTile = useCallback(() => {
    setDockOpen(true);
    requestAnimationFrame(() => {
      const carousel = document.getElementById('playlist-carousel');
      carousel
        ?.querySelector('[data-testid="playlist-item"], [data-testid="playlist-empty-add"]')
        ?.focus();
    });
  }, [setDockOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.altKey && event.code === 'KeyP') {
        event.preventDefault();
        focusFirstTile();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusFirstTile]);

  const addDisabledReason = locked ? COPY.lockedTooltip : capReached ? COPY.capTooltip : null;

  const handleAdd = useCallback((design, sourceEl) => {
    if (locked) return;
    if (items.length >= PLAYLIST_MAX_ITEMS) {
      toast.warning(COPY.capToast);
      announce(copy.full());
      return;
    }
    const item = addToPlaylist(design);
    haptics.light();
    announce(copy.added(item.title, items.length + 1));
    if (!reduceMotion && sourceEl) {
      setFlight({
        itemKey: dockOpen ? item.itemKey : 'peek',
        from: sourceEl.getBoundingClientRect(),
        thumbnailUrl: item.thumbnailUrl,
      });
    }
  }, [locked, items.length, toast, announce, addToPlaylist, reduceMotion, dockOpen]);

  const deliverReason = locked
    ? COPY.deliverBusy
    : !canvaConnected
      ? COPY.deliverDisconnected
      : items.length === 0
        ? COPY.deliverEmpty
        : !hasOnlineStation
          ? COPY.noStationOnline
          : null;

  const handleDeliver = () => {
    if (deliverReason) return;
    openDeliverConfirmation();
  };

  const agentId = delivery.agentId;
  const deliveryPanelProps = ['sending', 'building', 'done', 'failed'].includes(delivery.status) ? {
    delivery,
    items,
    stationName: stationLabel(linkedAgents, agentId),
    stationOnline: isStationOnline(linkedAgents, agentId),
    libraryText: describeLibraryLine(stationSettings[agentId], stationSettingsStatus)?.text ?? null,
    compact: !isWide,
    onRetry: retryPlaylistDelivery,
    onChooseStation: openDeliverConfirmation,
    onDeliverAgain: openDeliverConfirmation,
    onSignIn: () => clerk.redirectToSignIn({ redirectUrl: window.location.href }),
  } : null;

  const deliveredCaption = delivery.status === 'done' && delivery.deliveredAt
    ? deliveredAtCaption(new Date(delivery.deliveredAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }))
    : null;

  const deliverButton = (variant) => (
    <Button
      onClick={handleDeliver}
      disabled={Boolean(deliverReason)}
      variant="primary"
      size={{ header: 'lg', sheet: 'md', peek: 'sm' }[variant]}
      aria-describedby={deliverReason ? DELIVER_REASON_ID : undefined}
      data-testid="playlist-deliver-button"
      className={`whitespace-nowrap ${variant === 'sheet' ? 'w-full' : ''}`}
    >
      <Send className="w-4 h-4 mr-2" />
      {variant === 'peek' ? COPY.deliverShort : COPY.deliver}
    </Button>
  );

  const deliverReasonText = (visible = true) => deliverReason && (
    <p
      id={DELIVER_REASON_ID}
      className={visible ? 'text-caption text-gray-500 dark:text-gray-400 max-w-[18rem]' : 'sr-only'}
    >
      {deliverReason}
    </p>
  );

  const saveLabel = saving ? SAVED_COPY.saving : isSaved && !playlist.dirty ? SAVED_COPY.saved : SAVED_COPY.save;
  const savedControls = (
    <div className="flex flex-col items-start gap-1 pt-[18px]">
      <div className="flex items-center gap-2">
        <Button
          onClick={() => setDrawerOpen(true)}
          variant="secondary"
          size={isWide ? 'lg' : 'md'}
          aria-haspopup="dialog"
          data-testid="playlist-saved-open"
          className="whitespace-nowrap"
        >
          <Library className="w-4 h-4 mr-2" aria-hidden="true" />
          {SAVED_COPY.drawerTitle}
        </Button>
        {savingAvailable && (
          <Button
            onClick={handleSave}
            variant="secondary"
            size={isWide ? 'lg' : 'md'}
            loading={saving}
            disabled={!canSave || (isSaved && !playlist.dirty)}
            data-testid="playlist-save-button"
            data-state={saving ? 'saving' : isSaved && !playlist.dirty ? 'saved' : 'dirty'}
            className="whitespace-nowrap min-w-[6.5rem]"
          >
            {!saving && (isSaved && !playlist.dirty
              ? <Check className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" strokeWidth={3} aria-hidden="true" />
              : <Save className="w-4 h-4 mr-2" aria-hidden="true" />)}
            {saveLabel}
          </Button>
        )}
      </div>
      {savingAvailable && isSaved && (
        <p className="text-caption text-gray-500 dark:text-gray-400" data-testid="playlist-saved-caption">
          {savedCaption(playlist.savedUpdatedAt, now)}
        </p>
      )}
    </div>
  );

  const restoredDay = playlistRestoredFrom ? formatWeekdayMonthDay(new Date(playlistRestoredFrom)) : null;

  return (
    <MotionConfig reducedMotion="user">
      <a
        href="#playlist-carousel"
        onClick={(event) => {
          event.preventDefault();
          focusFirstTile();
        }}
        data-testid="playlist-skip-link"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:squircle-sm focus:bg-primary-600 focus:text-white focus:shadow-soft-lg"
      >
        {COPY.skipLink}
      </a>

      <main className="min-h-screen safe-top" data-testid="playlist-planner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="sr-only">{plannerHeading(playlist.name)}</h1>

          <AnimatePresence>
            {!canvaConnected && (
              <PlaylistBanner key="canva" tone="warning" Icon={Link2Off} testId="playlist-canva-banner">
                {COPY.canvaDisconnected}
              </PlaylistBanner>
            )}
            {playlistInterrupted && (
              <PlaylistBanner
                key="interrupted"
                tone="warning"
                Icon={AlertTriangle}
                testId="playlist-interrupted-banner"
                onDismiss={dismissInterruptedDelivery}
              >
                {COPY.interrupted}
              </PlaylistBanner>
            )}
            {restoredDay && items.length > 0 && (
              <PlaylistBanner
                key="restored"
                Icon={History}
                testId="playlist-restored-banner"
                actionLabel={COPY.restoredAction}
                onAction={clearPlaylist}
                onDismiss={dismissRestoredPlaylist}
              >
                {restoredBanner(restoredDay)}
              </PlaylistBanner>
            )}
          </AnimatePresence>

          <PlaylistHeader
            name={playlist.name}
            serviceDate={playlist.serviceDate}
            readOnly={locked}
            deliveredCaption={deliveredCaption}
            onMetaChange={setPlaylistMeta}
            savedControls={savedControls}
            deliverControl={isWide && (
              <div className="flex flex-col items-start md:items-end md:ml-auto gap-1 pt-[18px]">
                {deliverButton('header')}
                {deliverReasonText()}
              </div>
            )}
          />

          <PlaylistPicker
            canvaConnected={canvaConnected}
            countsByDesignId={countsByDesignId}
            addDisabledReason={addDisabledReason}
            onAdd={handleAdd}
            searchInputRef={searchInputRef}
            searchPulse={searchPulse && !reduceMotion}
          />
        </div>
        <div aria-hidden="true" className={isWide ? (dockOpen ? (deliveryPanelProps ? 'h-[340px]' : 'h-[232px]') : 'h-16') : 'h-16'} />
      </main>

      <PlaylistCarousel
        layout={layout}
        open={dockOpen}
        onOpenChange={setDockOpen}
        locked={locked}
        canvaConnected={canvaConnected}
        arrivingKey={flight?.itemKey}
        onFocusSearch={focusSearch}
        announce={announce}
        deliveryPanelProps={deliveryPanelProps}
        deliverControl={!isWide && (
          <div className={sheetOpen ? 'space-y-1' : 'flex-shrink-0'}>
            {deliverButton(sheetOpen ? 'sheet' : 'peek')}
            {deliverReasonText(sheetOpen)}
          </div>
        )}
      />

      {flight && <AddFlight flight={flight} onLanded={handleLanded} />}

      <DeliverConfirmSheet
        isOpen={confirming}
        layout={layout}
        playlist={playlist}
        stations={linkedAgents}
        stationSettings={stationSettings}
        stationSettingsStatus={stationSettingsStatus}
        defaultAgentId={playlist.targetAgentId}
        lastDeliveredAt={delivery.deliveredAt ?? (playlist.lastDeliveredAt ? Date.parse(playlist.lastDeliveredAt) : null)}
        returnFocusRef={confirmReturnRef}
        onConfirm={handleConfirm}
        onClose={cancelDeliverConfirmation}
      />

      <SavedPlaylistsDrawer
        isOpen={drawerOpen}
        layout={layout}
        playlists={savedPlaylists}
        status={savedPlaylistsStatus}
        currentId={isSaved ? playlist.id : null}
        draftNeedsConfirm={draftNeedsConfirm}
        canSave={canSave}
        deletingIds={deletingIds}
        onOpen={handleOpenSaved}
        onSaveThen={handleSaveThen}
        onDelete={handleDeleteSaved}
        onNew={handleNewPlaylist}
        onRetry={fetchPlaylists}
        onClose={() => setDrawerOpen(false)}
      />

      <TileConfirmPopover
        isOpen={Boolean(saveIssue)}
        anchorRect={saveIssue?.rect}
        message={saveIssue?.kind === 'conflict' ? SAVED_COPY.conflict : SAVED_COPY.goneTitle}
        confirmLabel={saveIssue?.kind === 'conflict' ? SAVED_COPY.keepMine : SAVED_COPY.saveAsNew}
        confirmVariant="primary"
        secondaryAction={saveIssue?.kind === 'conflict'
          ? { label: SAVED_COPY.useSaved, onClick: () => resolveSaveIssue('useSaved'), testId: 'playlist-save-use-saved' }
          : null}
        onConfirm={() => resolveSaveIssue('mine')}
        onCancel={() => setSaveIssue(null)}
        testId="playlist-save-issue"
      />

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-testid="playlist-live-region">
        <span key={message.id}>{message.text}</span>
      </div>
      <div aria-live="assertive" aria-atomic="true" className="sr-only" data-testid="playlist-alert-region">
        <span key={alert.id}>{alert.text}</span>
      </div>
    </MotionConfig>
  );
}
